import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLastNameFirst } from "../../../hooks/useNameFormat";
import type { NurseResult } from "../hooks";
import { CollapsibleSummaryBox, CopyableSummaryBox } from "./LoadResultDisplay";
import { buildNursesSummaryText } from "./nursesSummaryText";

type DuplicateOccurrence = {
	index: number;
	name: string;
	original?: string;
};

export type DuplicateDetail = {
	phone?: string;
	email?: string;
	occurrences: DuplicateOccurrence[];
	total_count: number;
};

export type DepartmentRolesUpdate = {
	roles_added: string[];
	default_role_set: string | null;
	role_groups_set: Record<string, string[]> | null;
};

export type LoadNursesResults = {
	results: NurseResult[];
	/** `held` rows are counted apart: they neither succeeded nor failed. */
	summary: { total: number; success: number; held: number; failed: number };
	/** The mode the upload ran in — read from the response, so toggling the file's mode afterwards changes nothing. */
	is_update: boolean;
	validation_summary?: string | null;
	duplicate_cleanup?: {
		duplicate_phones_cleaned: number;
		duplicate_emails_cleaned: number;
		phone_details: DuplicateDetail[];
		email_details: DuplicateDetail[];
	};
	department_roles_update?: DepartmentRolesUpdate;
};

export function NursesSummary({ results, timezone }: { results: LoadNursesResults; timezone?: string }) {
	const { t } = useTranslation();
	const lastNameFirst = useLastNameFirst();
	const summaryText = useMemo(() => buildNursesSummaryText(results.results, results.summary.total, t, lastNameFirst), [results, t, lastNameFirst]);
	return (
		<>
			<CopyableSummaryBox text={summaryText} title={t("onboarding_load.summary.title")} />
			{results.validation_summary && (
				<CollapsibleSummaryBox text={results.validation_summary} title={t("onboarding_load.validation_summary.title")} timezone={timezone} />
			)}
		</>
	);
}
